import { useEffect, useMemo, useState } from 'react';

import { ConsoleAPIGraph, CreativeHubTypes } from '@adflow/graphqlgen';
import { AdElement, AdflowAPI, SoccerStages, isTextElement } from '@adflow/types';
import { sampleAnimationStyles, type ElementAnimationStyle } from '@adflow/utils';

import { useMappedElements } from './mapElementDataPoints';
import { resolveAdElementsCore } from './resolveAdElementsCore';
import type { AdResolutionFontDescriptor } from './types';
import { useLoadFontsForAd } from './useFonts';
import { useAnimationPlayback } from './useAnimationPlayback';
import type { SoccerFixture } from './soccerPreview';
import { EMPTY_RESOLUTION_FONTS } from './fontConstants';

export type UseResolvedAdElementsArgs = {
  elements: AdElement[];
  brand: ConsoleAPIGraph.Brand | null;
  brandKit?: CreativeHubTypes.Brand | null;
  fonts: readonly AdResolutionFontDescriptor[];
  displayOptions?: AdflowAPI.AdTemplateDisplayOptions | null;
  customizations?: Record<string, string> | null;
  dataSources?: Array<{ id: string; type: string }>;
  activeSoccerFixture?: SoccerFixture | null;
  resolveSoccerCustomAssetUrls?: boolean;
  animation?: AdflowAPI.Animation | null;
  animationPlaybackToken?: number;
  playheadSeconds?: number | null;
  loadFonts?: boolean;
};

export type UseResolvedAdElementsResult = {
  resolvedElements: AdElement[];
  animationOverrides: ReadonlyMap<string, ElementAnimationStyle> | null;
};

export function useResolvedAdElements({
  elements,
  brand,
  brandKit,
  fonts,
  displayOptions,
  customizations,
  dataSources,
  activeSoccerFixture,
  resolveSoccerCustomAssetUrls = true,
  animation,
  animationPlaybackToken = 0,
  playheadSeconds = null,
  loadFonts = true,
}: UseResolvedAdElementsArgs): UseResolvedAdElementsResult {
  const [previewTick, setPreviewTick] = useState(0);
  const playback = useAnimationPlayback(animation, animationPlaybackToken);

  const animationOverrides = useMemo(() => {
    if (animation == null) {
      return null;
    }
    const time = playheadSeconds != null ? playheadSeconds : playback.currentTime;
    if (time == null) {
      return null;
    }
    return sampleAnimationStyles(animation, time);
  }, [animation, playheadSeconds, playback.currentTime]);

  useEffect(() => {
    if (activeSoccerFixture?.stage !== SoccerStages.PRE_EVENT) {
      return;
    }
    const intervalId = window.setInterval(() => {
      setPreviewTick((current) => current + 1);
    }, 1000);
    return () => window.clearInterval(intervalId);
  }, [activeSoccerFixture]);

  const preMapped = useMemo(
    () =>
      resolveAdElementsCore({
        elements,
        brandKit,
        fonts,
        customizations,
      }),
    [elements, brandKit, fonts, customizations],
  );

  const mappedElements = useMappedElements(
    preMapped,
    displayOptions,
    brand,
    dataSources,
    activeSoccerFixture,
    resolveSoccerCustomAssetUrls,
    previewTick,
  );

  const fontsForLoad = loadFonts ? fonts : EMPTY_RESOLUTION_FONTS;
  useLoadFontsForAd(loadFonts ? mappedElements : [], fontsForLoad);

  return {
    resolvedElements: mappedElements,
    animationOverrides,
  };
}
